import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import {
  ChangeStatusPayload,
  Requisition,
  RequisitionCourse,
} from '../interfaces/requisition.interfaces';
import { RequisitionResponse } from '../interfaces/requisitionTable.interfaces';
import { UserPayload } from '../interfaces/user.interface';
import { TypeEducation } from '../interfaces/type-educations.interface';
import { FilesPayload } from '../interfaces/files.interface';
import {
  Person,
  CreatePersonRequisitionPayload,
  PersonSearch,
} from '../interfaces/person.interfaces';
import { AccessState } from '../../../../types';
import { setUserPersonRequisition, setPersonRequisition } from './requisitionsSlice';
import { UserPerson, UserPersonPayload } from '../interfaces/userPerson.interface';
import { Course } from '../../courses/interfaces/course.interface';
import { Status2 } from '../../maintenance-product/interfaces/usetypes.interface';
import { IResponse } from '../../../shared/interfaces/response.interface';

export const requisitionTeacherTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Requisitions', 'Users'],
});
export const requisitionTeacherApi = requisitionTeacherTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @Get Teacher Requisitions
     */
    getRequisitions: builder.query<
      RequisitionResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        faculty_id?: string | number;
        start_date?: any;
        end_date?: any;
        status?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async (
        {
          profileId,
          page,
          perPage,
          search,
          faculty_id,
          start_date,
          end_date,
          status,
          sort,
          type_sort,
        },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get('/application/get-applications-next-status/pending', {
            params: {
              user_profile_id: profileId,
              page,
              acronym: 'REQDOC',
              size: perPage,
              search,
              faculty_id,
              start_date,
              end_date,
              status,
              ...(type_sort && { type_sort }),
              ...(sort && { sort }),
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Requisitions', id } as const)),
              { type: 'Requisitions', id: 'LIST' },
            ]
          : [{ type: 'Requisitions', id: 'LIST' }],
    }),

    /**
     * @GET type education availables
     */
    getAvailablesTypeEducations: builder.query<TypeEducation[], { profileId?: number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get('/type-educations', {
            params: {
              user_profile_id,
              data: 'all',
            },
          });

          if (!Array.isArray(data)) {
            return { error: 'No se ha encontrado tipo de educación' };
          }

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET get requisition by Id
     */
    getRequisitionById: builder.query<Requisition, { profileId: number; id: string }>({
      queryFn: async ({ profileId, id }) => {
        try {
          const { data } = await mainApi.get<Requisition>(`/application/${id}`, {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'Requisitions', id: 'EDIT' }],
    }),

    /**
     * @GET get persons
     */
    getPersonInformation: builder.query<PersonSearch, { profileId?: number; id: string }>({
      queryFn: async ({ profileId, id }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<PersonSearch>(`/persons`, {
            params: {
              identify: id,
              user_profile_id,
            },
          });

          dispatch(setPersonRequisition(data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST Create Requisition
     */
    sendRequisition: builder.mutation<
      RequisitionResponse,
      { profileId: number; requisitionPayload: Partial<any> }
    >({
      queryFn: async ({ profileId, requisitionPayload }) => {
        try {
          const { data } = await mainApi.post<RequisitionResponse>(
            '/applications/teacher-requisition',
            {
              user_profile_id: profileId,
              ...requisitionPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Requisitions', id: 'LIST' }],
    }),

    /**
     * @POST Change status requisition
     */
    changeStatus: builder.mutation<
      RequisitionResponse,
      { profileId: number; changeStatusPayload: Partial<ChangeStatusPayload> }
    >({
      queryFn: async ({ profileId, changeStatusPayload }) => {
        try {
          const { data } = await mainApi.post<RequisitionResponse>(
            '/teacher-requisition/change-status',
            {
              user_profile_id: profileId,
              ...changeStatusPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Requisitions', id: 'LIST' },
        { type: 'Requisitions', id: 'EDIT' },
      ],
    }),

    /**
     * @POST Create Person and User in requisition
     */
    createPersonAndUserRequisition: builder.mutation<
      UserPersonPayload,
      { profileId?: number; createPersonAndUserPayload: Partial<UserPerson> }
    >({
      queryFn: async ({ profileId, createPersonAndUserPayload }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.post<UserPersonPayload>('/persons/users', {
            user_profile_id,
            ...createPersonAndUserPayload,
          });
          dispatch(setUserPersonRequisition(data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST Create Person in requisition
     */
    createPersonRequisition: builder.mutation<
      Person,
      { profileId: number; createPersonPayload: Partial<CreatePersonRequisitionPayload> }
    >({
      queryFn: async ({ profileId, createPersonPayload }, { dispatch }) => {
        try {
          const { data } = await mainApi.post<Person>('/persons', {
            user_profile_id: profileId,
            ...createPersonPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST Create user
     */
    createUserRequisition: builder.mutation<
      UserPayload,
      { profileId: number; createUserPayload: Partial<UserPayload> }
    >({
      queryFn: async ({ profileId, createUserPayload }, { dispatch }) => {
        try {
          const { data } = await mainApi.post<UserPayload>('/users', {
            user_profile_id: profileId,
            ...createUserPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),

    /**
     * @POST Create user
     */
    sendFilesRequisition: builder.mutation<
      String,
      { filePayload: FilesPayload; profileId: number }
    >({
      queryFn: async ({ filePayload, profileId }) => {
        try {
          const form = new FormData();
          form.append('files[]', filePayload.files || '');
          form.append('type_document', '12');
          form.append('user_profile_id', String(profileId));
          const { data } = await mainApi.post(`/teacher-requisition/upload/files`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getCoursesRequisition: builder.query<
      Course[],
      { profileId: number; educationLevelId: number; periodId: number; campusId: number }
    >({
      queryFn: async ({ profileId, periodId, educationLevelId, campusId }) => {
        try {
          const { data } = await mainApi.get<Course[]>(
            `/courses/period/${periodId}/education-level/${educationLevelId}`,
            {
              params: {
                user_profile_id: profileId,
                campus_id: campusId,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getRequisitionPDF: builder.query<any, { profileId: number; appCode: string }>({
      queryFn: async ({ profileId, appCode }) => {
        try {
          const { data } = await mainApi.get<any>(`/teacher-requisition/${appCode}/pdf`, {
            responseType: 'arraybuffer',
            headers: {
              Accept: 'application/pdf',
              'Content-Type': 'application/json',
            },
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getRequisitionCourses: builder.query<
      RequisitionCourse[],
      { profileId?: number; appCode: string }
    >({
      queryFn: async ({ profileId, appCode }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<RequisitionCourse[]>(
            `/teacher-requisition/${appCode}/courses`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    //

    getAcademicFormationStatuses: builder.query<Status2[], {}>({
      queryFn: async (_, { getState }) => {
        try {
          const { access } = getState() as { access: AccessState };

          const user_profile_id = access.currentProfile?.profileId;

          const { data } = await mainApi.get<IResponse<Status2>>('/status', {
            params: { category_status_id: 6, user_profile_id, data: 'all' },
          });

          const statusArray =
            data?.data?.filter((st) => st.st_name === 'Aprobado' || st.st_name === 'Cursando') ||
            [];

          return { data: statusArray };
        } catch (error: any) {
          return { error };
        }
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
  useGetRequisitionsQuery,
  useGetAvailablesTypeEducationsQuery,
  useSendRequisitionMutation,
  useChangeStatusMutation,
  useSendFilesRequisitionMutation,
  useGetPersonInformationQuery,
  useLazyGetPersonInformationQuery,
  useCreatePersonRequisitionMutation,
  useCreateUserRequisitionMutation,
  useGetRequisitionByIdQuery,
  useCreatePersonAndUserRequisitionMutation,
  useGetCoursesRequisitionQuery,
  useLazyGetRequisitionPDFQuery,

  useGetRequisitionCoursesQuery,

  useGetAcademicFormationStatusesQuery,
} = requisitionTeacherApi;
